import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, message, List, Popconfirm, Button } from 'antd';
import dayjs from 'dayjs';
import followUpService, { FollowUpRecord } from '../../services/followUp';
import { STATUS_OPTIONS } from '../../config/follow-up-options';

interface FollowUpRecordModalProps {
  open: boolean;
  followUpId?: number;
  onClose: () => void;
}

/**
 * 跟进记录弹窗：展示历史记录并支持新增记录。
 */
const FollowUpRecordModal: React.FC<FollowUpRecordModalProps> = ({ open, followUpId, onClose }) => {
  const [form] = Form.useForm();
  const [records, setRecords] = React.useState<FollowUpRecord[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const getStatusLabel = (value?: string) => {
    if (!value) return '-';
    const option = STATUS_OPTIONS.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const loadRecords = async () => {
    if (!followUpId) return;
    setLoading(true);
    try {
      const res = await followUpService.getRecords(followUpId);
      if (res.success) {
        const list = res.data || [];
        setRecords(list);
        if (list.length > 0) {
          const latest = list[0];
          form.setFieldsValue({ status: latest.status || '跟进中' });
        } else {
          form.setFieldsValue({ status: '跟进中' });
        }
      }
    } catch (e) {
      message.error('加载记录失败');
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async () => {
    if (!followUpId) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await followUpService.addRecord(followUpId, values);
      if (res.success) {
        message.success('已新增记录');
        form.resetFields();
        await loadRecords();
        onClose();
      } else {
        message.error(res.message || '新增失败');
      }
    } catch (e) {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recordId?: number) => {
    if (!followUpId || !recordId) return;
    try {
      setSaving(true);
      const res = await followUpService.deleteRecord(followUpId, recordId);
      if (res.success) {
        message.success('已删除记录');
        await loadRecords();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除记录失败');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadRecords();
    }
  }, [open, followUpId]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={saving}
      title="跟进记录"
      width={600}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4, padding: 12 }}>
          <List
            loading={loading}
            dataSource={records}
            locale={{ emptyText: '暂无记录' }}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Popconfirm key="delete" title="确定删除这条记录吗？" onConfirm={() => handleDelete(item.id)}>
                    <Button type="link" danger size="small">删除</Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={<span>{getStatusLabel(item.status) || '未标记状态'}</span>}
                  description={
                    <div>
                      <div style={{ color: '#666', fontSize: 14 }}>{item.remark || '无备注'}</div>
                      <div style={{ color: '#999', fontSize: 12 }}>{item.created_at ? dayjs(item.created_at).format('YYYY-MM-DD') : ''}</div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        </div>

        <div>
          <Form form={form} layout="vertical">
            <Form.Item label="跟进状态" name="status" rules={[{ required: true, message: '请选择跟进状态' }]}>
              <Select placeholder="选择跟进状态">
                {STATUS_OPTIONS.map(option => (
                  <Select.Option key={option.label} value={option.label}>{option.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="跟进记录" name="remark" rules={[{ max: 500, message: '最多500字' }]}>
              <Input.TextArea rows={6} placeholder="输入跟进记录" />
            </Form.Item>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default FollowUpRecordModal;
